import type { TAttributeValueArrayPair } from '@timeedit/types/lib/types';
import { Icon } from '@timeedit/ui-components';
import { Button, Input, Space } from '@timeedit/ui-components/antd';

type Props = {
  attributePairs: TAttributeValueArrayPair[];
  onChange: (value: TAttributeValueArrayPair[]) => void;
  hasError?: boolean;
};

type RowProps = {
  pair: TAttributeValueArrayPair;
  onChange: (newVal: TAttributeValueArrayPair) => void;
  onDeleteRow: () => void;
  allowDelete: boolean;
  hasError?: boolean;
};

const AttributePairCellRow = ({
  pair,
  onChange,
  onDeleteRow,
  allowDelete,
  hasError,
}: RowProps) => {
  const onUpdateCell = (newVal: string, cellIdx: number) => {
    const updatedRow: TAttributeValueArrayPair = [
      cellIdx === 0 ? newVal : pair[0],
      cellIdx === 1 ? newVal : pair[1],
    ];
    onChange(updatedRow);
  };

  return (
    <Space direction="horizontal">
      <Input
        value={pair[0]}
        onChange={(e) => onUpdateCell(e.target.value, 0)}
        size="small"
        style={{ width: '100px' }}
        placeholder="Attribute"
        status={hasError && !pair[0] ? 'error' : undefined}
      />
      <Input
        value={pair[1]}
        onChange={(e) => onUpdateCell(e.target.value, 1)}
        size="small"
        style={{ width: '100px' }}
        placeholder="Value"
        status={hasError && !pair[1] ? 'error' : undefined}
      />
      <Button
        type="link"
        danger
        ghost
        onClick={onDeleteRow}
        disabled={allowDelete}
        icon={<Icon id="DeleteOutlined" alt="Delete" />}
        style={{ width: '16px' }}
      />
    </Space>
  );
};

const AttributePairTableCell = ({
  attributePairs,
  onChange,
  hasError,
}: Props) => {
  const onAddRow = () => {
    onChange([...attributePairs, ['', '']]);
  };

  const onDeleteRow = (rowIdx: number) => {
    onChange([
      ...attributePairs.slice(0, rowIdx),
      ...attributePairs.slice(rowIdx + 1),
    ]);
  };

  const onUpdateRow = (newValue: TAttributeValueArrayPair, rowIdx: number) => {
    const updatedVal = [
      ...attributePairs.slice(0, rowIdx),
      newValue,
      ...attributePairs.slice(rowIdx + 1),
    ];
    onChange(updatedVal);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div>
        {attributePairs.map((pair, rowIdx) => (
          <AttributePairCellRow
            pair={pair}
            key={`attr-value-pair-${rowIdx}`}
            onChange={(newVal) => onUpdateRow(newVal, rowIdx)}
            onDeleteRow={() => onDeleteRow(rowIdx)}
            allowDelete={!attributePairs.length || attributePairs.length <= 1}
            hasError={hasError}
          />
        ))}
      </div>
      <Button
        onClick={() => onAddRow()}
        type="link"
        style={{ alignSelf: 'flex-end' }}
      >
        + Add pair
      </Button>
    </div>
  );
};

export default AttributePairTableCell;
